import * as React from 'react';

import {
    ApplicationHelpType,
    CaseFilterType,
    FavoriteTypesEnum,
    OrganizationTypesEnum,
    SortColumns,
    SortDirections,
    UserRightsEnum,
    ZoneContextType,
} from '../../utilities/Constants';
import {
    CheckmateCaseHistoryIcon,
    CheckmateEasyUpdateIcon,
    CheckmateSameCaseGroupIcon,
} from '../../shared/CheckmateIcons';
import {
    ICaseFilterModel,
    ICaseModel,
    ICaseSearchModel,
    ICaseStatusLookupModel,
} from '../../interfaces/ICase';
import { ILookupModel, IMultiSelectOption } from '../../interfaces/ILookup';
import { Offcanvas, Stack, Table } from 'react-bootstrap';

import Authorization from '../../stores/Authorization';
import { CaseAuditControl } from '../../components/shared/modals/CaseAuditControl';
import CaseEasyUpdate from '../../components/case/easy-update/CaseEasyUpdate';
import { CaseListAppliedFilters } from '../../components/case-list/CaseListAppliedFilters';
import { CaseListSearchFilters } from '../../components/case-list/CaseListSearchFilters';
import { CheckmatePagination } from '../quad/CheckmatePagination';
import Common from '../../stores/Common';
import { DefaultCaseSearchModel } from './CaseListPageWrapper';
import { Favorite } from '../../components/shared/Favorite';
import { Help } from '../../components/shared/Help';
import { IBasePagedModel } from '../../interfaces/PagedSortedSearch';
import { IFavoriteModel } from '../../interfaces/IFavorite';
import { IOrganizationModel } from '../../interfaces/IOrganization';
import { IPagedQueryModel } from '../../interfaces/IQuery';
import { IUserModel } from '../../interfaces/IUser';
import { IValidation } from '../../interfaces/IError';
import { Link } from 'react-router-dom';
import { Loader } from '../../components/shared/Loader';
import { LocalRoutes } from '../../utilities/LocalRoutes';
import { MainContentOverlayComponent } from '../../components/shared/MainContentOverlay';
import { PageWithAffixedHeader } from '../../shared/PageWithAffixedHeader';
import { SortableHeader } from '../../components/shared/SortableHeader';
import { cloneDeep } from 'lodash';
import { getHotkeyHandler } from '@mantine/hooks';

const pageTitle = 'Case List';

interface ICaseListPageProps {
    cases: ICaseModel[];
    caseCount: number;
    appliedSearchModel: ICaseSearchModel;
    pendingSearchModel: ICaseSearchModel;
    defaultUserOrg: IOrganizationModel;
    isFetching: boolean;
    lookups: {
        caseManagers: IUserModel[];
        caseStatuses: ICaseStatusLookupModel[];
        diagnoses: ILookupModel[];
        favorites: IFavoriteModel[];
        jurisdictions: ILookupModel[];
        plaintiffsFirms: ILookupModel[];
        qScores: ILookupModel[];
        usStates: ILookupModel[];
    };
    onApplyFilters: (updatedSearchModel: ICaseSearchModel) => void;
    onResetFilters: () => void;
    onSearch: (pendingSearchModel: ICaseSearchModel, fromCurrentPage?: boolean) => void;
    onUpdateCase: (updatedCase: ICaseModel) => void;
    refreshIdentifier: string;
    reloadFavorites: () => void;
    user: IUserModel;
    zoneContext: ZoneContextType;
}

interface ICaseListState {
    assessmentCaseName?: string;
    assessmentSQScore?: string;
    currentFavoriteDetailsString: string;
    easyUpdateAttemptedCaseList: string[];
    easyUpdateCaseGuid?: string;
    easyUpdateMode: boolean;
    enablePlanningExecution?: boolean;
    isSystemAdmin: boolean;
    pendingResponse: boolean;
    searchText: string;
    selectedEasyUpdateFields: IMultiSelectOption[];
    showCaseSearchByNameNumberInfo?: boolean;
    showFilterPane: boolean;
    showHistory: boolean;
    selectedCaseGuidToDisplayHistory?: string;
    validation: IValidation;
}

export class CaseListPage extends React.Component<ICaseListPageProps, ICaseListState> {
    DefaultPageSize = DefaultCaseSearchModel.queryModel!.pageSize;
    tableHeadRef = React.createRef<HTMLTableSectionElement>();

    constructor(props: ICaseListPageProps) {
        super(props);

        this.state = {
            currentFavoriteDetailsString: '',
            easyUpdateAttemptedCaseList: [],
            easyUpdateMode: false,
            isSystemAdmin: false,
            pendingResponse: true,
            searchText: '',
            selectedEasyUpdateFields: [],
            showFilterPane: false,
            showHistory: false,
            validation: {},
        };
    }

    componentDidMount() {
        const isAuthorized = Authorization.isAuthorizedToRoute(
            LocalRoutes.Cases,
            this.props.user,
            this.props.zoneContext.zoneGuid
        );

        if (!isAuthorized) {
            window.location.assign(LocalRoutes.AccessDenied);
        }
    }

    componentDidUpdate(prevProps: ICaseListPageProps) {
        if (this.props.refreshIdentifier !== prevProps.refreshIdentifier) {
            this.props.onResetFilters();
        }

        if (!this.props.isFetching && prevProps.isFetching) {
            this.setState({ showFilterPane: false });
            this.tableHeadRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }

    getFilterTextFromLookup = (
        displayLabel: string,
        filters: ICaseFilterModel[],
        lookup: ILookupModel[]
    ) => {
        let text = '';
        if (filters.length > 0) {
            text += displayLabel + ':' + '\n';

            filters.forEach((filter) => {
                const found = lookup.find((item) => item.name === filter.text);

                if (found) {
                    text += (found.displayName ?? found.name) + '\n';
                }
            });

            text += '\n';
        }
        return text;
    };

    handleDefaultFavoriteChange = (settingJson?: string) => {
        if (settingJson) {
            const model = JSON.parse(settingJson);
            this.setState({ currentFavoriteDetailsString: model });

            this.props.reloadFavorites();
        }
    };

    handleFavoriteSelected = (settingJson?: string) => {
        let selectedFilters = '';
        if (settingJson) {
            const favoriteJson: ICaseSearchModel = JSON.parse(settingJson);

            if (favoriteJson.statusIds) {
                const selectedStatusOptions: IMultiSelectOption[] = [];

                for (let i = 0; i < favoriteJson.statusIds.length; i++) {
                    const statusId = favoriteJson.statusIds[i];
                    let matches = this.props.lookups.caseStatuses.filter(
                        (x) => x.status.id === statusId && (!x.subStatus || x.subStatus.id === 0)
                    );
                    if (
                        matches.length > 0 &&
                        !selectedStatusOptions.find((x) => x.id === matches[0].id)
                    )
                        selectedStatusOptions.push({
                            label: matches[0].name!,
                            value: matches[0].name!,
                            id: matches[0].id,
                        }); // Use id to hold the actual ICaseStatusLookupModel.Id unlike the other filters

                    if (favoriteJson.subStatusIds) {
                        for (let j = 0; j < favoriteJson.subStatusIds.length; j++) {
                            const subStatusId = favoriteJson.subStatusIds[j];
                            matches = this.props.lookups.caseStatuses.filter(
                                (x) =>
                                    x.status.id === statusId &&
                                    x.subStatus &&
                                    x.subStatus.id === subStatusId
                            );
                            if (
                                matches.length > 0 &&
                                !selectedStatusOptions.find((x) => x.id === matches[0].id)
                            )
                                selectedStatusOptions.push({
                                    label: matches[0].name!,
                                    value: matches[0].name!,
                                    id: matches[0].id,
                                }); // Use id to hold the actual ICaseStatusLookupModel.Id unlike the other filters
                        }
                    }
                }

                if (selectedStatusOptions.length > 0) {
                    selectedFilters += 'STATUS:' + '\n';
                    for (let i = 0; i < selectedStatusOptions.length; i++) {
                        selectedFilters += selectedStatusOptions[i].label + '\n';
                    }
                    selectedFilters += '\n';
                }
            }

            if (favoriteJson.filters) {
                const caseFilters: ICaseFilterModel[] = favoriteJson.filters;

                const diagnosisFilters = caseFilters.filter(
                    (x) => x.typeId === CaseFilterType.Diagnosis.Value
                );

                const usStateFilters = caseFilters.filter(
                    (x) => x.typeId === CaseFilterType.State.Value
                );

                const jurisdictionFilters = caseFilters.filter(
                    (x) => x.typeId === CaseFilterType.Jurisdiction.Value
                );

                const plaintiffFirmFilters = caseFilters.filter(
                    (x) => x.typeId === CaseFilterType.PlaintiffsFirm.Value
                );

                const caseManagerFilters = caseFilters.filter(
                    (x) => x.typeId === CaseFilterType.CaseManager.Value
                );

                const qScoreFilters = caseFilters.filter(
                    (x) => x.typeId === CaseFilterType.QScore.Value
                );

                selectedFilters += this.getFilterTextFromLookup(
                    'DISEASE',
                    diagnosisFilters,
                    this.props.lookups.diagnoses
                );

                selectedFilters += this.getFilterTextFromLookup(
                    'STATE',
                    usStateFilters,
                    this.props.lookups.usStates
                );

                selectedFilters += this.getFilterTextFromLookup(
                    'JURISDICTION',
                    jurisdictionFilters,
                    this.props.lookups.jurisdictions
                );

                selectedFilters += this.getFilterTextFromLookup(
                    'PLAINTIFFS FIRM/NATIONAL PLAINTIFFS FIRM',
                    plaintiffFirmFilters,
                    this.props.lookups.plaintiffsFirms
                );

                if (caseManagerFilters.length > 0) {
                    selectedFilters += 'A1:' + '\n';

                    caseManagerFilters.forEach((filter) => {
                        const found = this.props.lookups.caseManagers.filter(
                            (x) => x.guid === filter.text
                        );

                        const { profile } = found[0];
                        const name = profile?.firstName + ' ' + profile?.lastName;

                        if (found.length > 0) {
                            selectedFilters += name + '\n';
                        }
                    });

                    selectedFilters += '\n';
                }

                selectedFilters += this.getFilterTextFromLookup(
                    'SQ SCORE',
                    qScoreFilters,
                    this.props.lookups.qScores
                );
            }
        }

        this.setState({ currentFavoriteDetailsString: selectedFilters });
    };

    setEnablePlanningExecution = async () => {
        let enablePlanningExecution = false;

        if (this.props.defaultUserOrg.enablePlanningExecution) enablePlanningExecution = true;

        if (this.props.user && this.props.user.homeOrganization) {
            if (this.props.user.homeOrganization.guid === this.props.defaultUserOrg.guid) {
                enablePlanningExecution = false; // If user is an employee of the Portfolio organization, Disable Planning & Execution
            } else if (
                this.props.defaultUserOrg.primaryAssociatedOrganization &&
                this.props.defaultUserOrg.primaryAssociatedOrganization.guid ==
                    this.props.user.homeOrganization.guid
            ) {
                enablePlanningExecution = false; // If user is an employee of the Company organization, Disable Planning & Execution
            }
        }

        this.setState({ enablePlanningExecution });
    };

    checkForAtLeastOneFilter = (searchModel: ICaseSearchModel) => {
        const { statusIds = [], subStatusIds = [], filters = [], searchText = '' } = searchModel;

        return (
            [statusIds, subStatusIds, filters].some((arr) => arr.length > 0) ||
            searchText.trim().length > 0
        );
    };

    handleCaseSearchCriteriaChange = (
        event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
    ) => {
        const pendingSearchModelCopy = cloneDeep(this.props.pendingSearchModel);

        if (pendingSearchModelCopy.queryModel) {
            pendingSearchModelCopy.queryModel.pageNumber = 0;
        }

        pendingSearchModelCopy.searchText = event.target.value;

        this.props.onApplyFilters(pendingSearchModelCopy);
    };

    sortData = (sortBy: string, sortDirection: string) => {
        const appliedSearchModelCopy = cloneDeep(this.props.appliedSearchModel);
        if (appliedSearchModelCopy.queryModel) {
            appliedSearchModelCopy.queryModel.sortBy = parseInt(sortBy);
            appliedSearchModelCopy.queryModel.sortDirection =
                sortDirection === 'desc' ? SortDirections.Desc : SortDirections.Asc;
            appliedSearchModelCopy.queryModel.pageNumber = 0;
        }
        this.props.onSearch(appliedSearchModelCopy);
    };

    handleEasyUpdate = (e: React.FormEvent<HTMLButtonElement>, caseGuid: string) => {
        const easyUpdateAttemptedCaseList = this.state.easyUpdateAttemptedCaseList;
        easyUpdateAttemptedCaseList.push(caseGuid);
        this.setState({
            easyUpdateMode: true,
            easyUpdateCaseGuid: caseGuid,
            easyUpdateAttemptedCaseList: easyUpdateAttemptedCaseList,
        });
    };

    handleCaseHistoryClicked = (e: React.FormEvent<HTMLButtonElement>, caseGuid: string) => {
        this.setState({ showHistory: true, selectedCaseGuidToDisplayHistory: caseGuid });
    };

    easyUpdateAttempted = (caseGuid: string) => {
        return this.state.easyUpdateAttemptedCaseList.some((x) => x == caseGuid);
    };

    handleEasyUpdateDone = (updatedCase: ICaseModel) => {
        this.setState({ easyUpdateMode: false, easyUpdateCaseGuid: undefined }, () => {
            this.props.onUpdateCase(updatedCase);
        });
    };

    handleEasyUpdateCancel = () => {
        this.setState({
            easyUpdateMode: false,
            easyUpdateCaseGuid: undefined,
        });
    };

    goToPage = (pageNumber: number) => {
        const { appliedSearchModel } = this.props;
        if (!appliedSearchModel) return DefaultCaseSearchModel;

        const { queryModel = DefaultCaseSearchModel.queryModel } = appliedSearchModel;

        const newSearchModel = {
            ...appliedSearchModel,
            queryModel: {
                ...queryModel,
                pageNumber: pageNumber - 1,
            },
        };

        this.props.onSearch(newSearchModel, true);
    };

    mapToBasePagedModel = (query: IPagedQueryModel, totalResults: number): IBasePagedModel => {
        const offsetPage = query.pageNumber + 1;
        const computedFirstRecordIndex = offsetPage * query.pageSize - (query.pageSize - 1);

        const computedTotalRecords = query.pageNumber * query.pageSize + query.pageSize;
        const computedLastRecordIndex =
            computedTotalRecords > totalResults ? totalResults : computedTotalRecords;

        return {
            pageNumber: offsetPage,
            pageSize: query.pageSize,
            recordStartIndex: computedFirstRecordIndex,
            recordEndIndex: computedLastRecordIndex,
            totalResults,
            totalPages: Math.ceil(totalResults / query.pageSize),
        };
    };

    handlePageSizeChange = (pageSize: number) => {
        const searchModelCopy = cloneDeep(this.props.appliedSearchModel);
        searchModelCopy.queryModel.pageSize = pageSize;
        this.props.onSearch(searchModelCopy);
    };

    applyFavoriteSelected = (settingJson?: string) => {
        if (settingJson) {
            const favoriteFilters: ICaseSearchModel = JSON.parse(settingJson);
            this.props.onSearch(favoriteFilters);
        }
    };

    handleAreFavoriteFiltersValid = () => {
        const hasAtleastOneFilter = this.checkForAtLeastOneFilter(this.props.appliedSearchModel);

        let validation: IValidation = {
            favorites: [],
        };

        if (!hasAtleastOneFilter) {
            validation = {
                favorites: ['Enter at least 1 filter to Save Favorite View'],
            };
        }

        this.setState({
            validation,
            pendingResponse: false,
        });

        return hasAtleastOneFilter;
    };

    handleToggleFilterPane = () => {
        this.setState({ showFilterPane: !this.state.showFilterPane });
    };

    handleClearCaseNameOrNumber = () => {
        const searchModel = cloneDeep(this.props.appliedSearchModel);

        const filtersCount = searchModel.filters?.length ?? 0;
        const statusCount = searchModel.statusIds?.length ?? 0;
        const subStatusCount = searchModel.subStatusIds?.length ?? 0;

        const hasZeroFiltersApplied = filtersCount + statusCount + subStatusCount === 0;

        if (hasZeroFiltersApplied) {
            this.props.onSearch(DefaultCaseSearchModel);
        } else {
            searchModel.searchText = '';
            this.props.onSearch(searchModel);
        }
    };

    handleSearchByCaseNameOrNumber = () => {
        this.props.onSearch(this.props.pendingSearchModel);
    };

    getFavoriteSearchModelAsJSON = (searchModel: ICaseSearchModel) => {
        const searchModelCopy = cloneDeep(searchModel);
        searchModelCopy.queryModel.pageNumber = 0;
        return JSON.stringify(searchModelCopy);
    };

    render() {
        const showAddNewButton = Authorization.userHasRight(
            UserRightsEnum.AddNewCase,
            this.props.user,
            this.props.zoneContext.zoneGuid
        );

        const showEasyUpdateColumn = Authorization.isAuthorizedToRoute(
            LocalRoutes.CaseEasyUpdate,
            this.props.user,
            this.props.zoneContext.zoneGuid
        );

        const showCasePlan =
            this.state.enablePlanningExecution &&
            Authorization.userHasAtLeastOneRight(
                this.props.user,
                [
                    UserRightsEnum.FullPlanningAndExecution,
                    UserRightsEnum.ReadOnlyPlanningAndExecution,
                    UserRightsEnum.FirmLevelPlanningAndExecution,
                ],
                this.props.zoneContext.zoneGuid
            );

        const viewPackageSettlementColumn = Authorization.userHasRight(
            UserRightsEnum.ViewPackageSettlement,
            this.props.user,
            this.props.zoneContext.zoneGuid
        );

        const showThreatScoreColumn = Authorization.userHasRight(
            UserRightsEnum.ViewThreatScore,
            this.props.user,
            this.props.zoneContext.zoneGuid
        );

        const showAssessmentColumn = Authorization.userHasAtLeastOneRight(
            this.props.user,
            [
                UserRightsEnum.AssessmentBestFitCasesGet,
                UserRightsEnum.AssessmentCaseStatisticsGet,
                UserRightsEnum.AssessmentPredictedOutcomeGet,
                UserRightsEnum.AssessmentThreatScoreGet,
            ],
            this.props.zoneContext.zoneGuid
        );

        const isAuthorizedToUseEasyUpdate = Authorization.isAuthorizedToRoute(
            LocalRoutes.CaseEasyUpdate,
            this.props.user,
            this.props.zoneContext.zoneGuid
        );

        const favoritableSearchModelAsJSON = this.getFavoriteSearchModelAsJSON(
            this.props.appliedSearchModel
        );

        const hasSearchValue = (this.props.pendingSearchModel.searchText?.length ?? 0) > 0;

        const isLocalBasicUser = Authorization.isLocalScope(
            this.props.user,
            this.props.zoneContext.zoneGuid
        );

        const isCompanyUser =
            this.props.user?.homeOrganization?.organizationType?.id ===
            OrganizationTypesEnum.Company.Value;

        const showSameCaseColumn = !isLocalBasicUser && !isCompanyUser;

        return (
            <>
                {isAuthorizedToUseEasyUpdate && this.state.easyUpdateMode && (
                    <MainContentOverlayComponent>
                        <CaseEasyUpdate
                            defaultSelectedFields={this.state.selectedEasyUpdateFields}
                            documentTitle={'Case List | Easy Update'}
                            caseGuid={this.state.easyUpdateCaseGuid}
                            onComplete={this.handleEasyUpdateDone}
                            onCancel={this.handleEasyUpdateCancel}
                            onSelectedFieldsChange={(fields: IMultiSelectOption[]) => {
                                this.setState({ selectedEasyUpdateFields: fields });
                            }}
                            hidden={!this.state.easyUpdateMode}
                        />
                    </MainContentOverlayComponent>
                )}

                <Offcanvas
                    show={this.state.showFilterPane}
                    onHide={() => this.setState({ showFilterPane: false })}
                    placement="end"
                    scroll
                    backdrop
                >
                    <Offcanvas.Header
                        closeButton
                        style={{ borderBottom: '1px solid lightgray', height: '77px' }}
                    >
                        <Offcanvas.Title>
                            <span style={{ fontSize: 20, fontWeight: 500 }}>
                                {pageTitle} Filters
                            </span>
                        </Offcanvas.Title>
                    </Offcanvas.Header>
                    <Offcanvas.Body>
                        <CaseListSearchFilters
                            appliedSearchModel={this.props.appliedSearchModel}
                            isSearching={this.props.isFetching}
                            onApplyFilters={this.props.onSearch}
                        />
                    </Offcanvas.Body>
                </Offcanvas>

                <PageWithAffixedHeader
                    headerComponent={
                        <>
                            <Stack
                                direction="horizontal"
                                style={{ justifyContent: 'space-between' }}
                            >
                                <h1>Case List</h1>
                                <div className="float-end no-padding col-sm-8 d-flex flex-row-reverse align-items-center">
                                    {/* System Admin cannot create new case. Only National Counsel User can create a new case */}
                                    {showAddNewButton && (
                                        <Link
                                            to={this.props.zoneContext.buildZoneUrl(
                                                LocalRoutes.CaseFileNew
                                            )}
                                            style={{ textDecoration: 'none' }}
                                        >
                                            <span className="btn-green btn float-end btn-icon">
                                                <i className="fal fa-lg fa-plus color-white" />
                                            </span>
                                        </Link>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-gray btn-icon float-end"
                                        onClick={this.handleClearCaseNameOrNumber}
                                        disabled={!hasSearchValue}
                                    >
                                        <i className="fal fa-times color-white" />
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-black btn-icon float-end"
                                        onClick={this.handleSearchByCaseNameOrNumber}
                                        disabled={!hasSearchValue}
                                    >
                                        <i className="fal fa-search color-white" />
                                    </button>
                                    <input
                                        className="form-control horizontal-margin-right-sm"
                                        type="text"
                                        name="caseNameOrNumberSearch"
                                        placeholder="Search by Name or Number"
                                        value={this.props.pendingSearchModel.searchText ?? ''}
                                        onChange={this.handleCaseSearchCriteriaChange}
                                        onKeyDown={getHotkeyHandler([
                                            ['Enter', this.handleSearchByCaseNameOrNumber],
                                        ])}
                                    />
                                    <span>
                                        <Help
                                            type={ApplicationHelpType.Info}
                                            title="Search by Name or Number"
                                            helpText="You can search by Injured Party First Name, Last Name or case Number. For an exact match, you can search using the format: Last Name, First Name (with a space after the last name) ."
                                            additionalStyleClassNames="float-end"
                                        />
                                    </span>
                                    <Favorite
                                        applyFavoriteSelected={this.applyFavoriteSelected}
                                        currentFavoriteDetailsString={
                                            this.state.currentFavoriteDetailsString
                                        }
                                        currentFavoriteSettingJson={favoritableSearchModelAsJSON}
                                        onDefaultFavoriteChange={this.handleDefaultFavoriteChange}
                                        onFavoriteSelected={this.handleFavoriteSelected}
                                        onValidateFilters={this.handleAreFavoriteFiltersValid}
                                        type={{ id: FavoriteTypesEnum.CaseList }}
                                        user={this.props.user}
                                        zoneContext={this.props.zoneContext}
                                    />
                                </div>
                            </Stack>

                            <div className="text-danger">{this.state.validation.model}</div>
                            <div className="text-danger">{this.state.validation.favorites}</div>

                            <CaseListAppliedFilters
                                onApplyFilters={(updatedFilters: ICaseSearchModel) =>
                                    this.props.onSearch(updatedFilters)
                                }
                                onToggleFilterPane={this.handleToggleFilterPane}
                                searchModel={this.props.appliedSearchModel}
                            />
                        </>
                    }
                >
                    {this.props.isFetching && <Loader />}
                    {(!this.state.isSystemAdmin ||
                        (this.props.appliedSearchModel.organizationGuid?.length ?? 0 > 0)) && (
                        <>
                            <Table responsive size="sm" style={{ position: 'relative' }}>
                                <thead ref={this.tableHeadRef} className="mt-3 sticky-header">
                                    <tr style={{ verticalAlign: 'middle' }}>
                                        <SortableHeader
                                            headerText="Case Name"
                                            sortKey={SortColumns.CaseName.toString()}
                                            onSort={this.sortData}
                                            thClassName="col-sm-2"
                                            noWrap
                                        />
                                        <SortableHeader
                                            headerText="Case Number"
                                            sortKey={SortColumns.CaseNumber.toString()}
                                            onSort={this.sortData}
                                            thClassName="col-sm-1 text-nowrap"
                                            noWrap
                                        />
                                        {showSameCaseColumn && (
                                            <SortableHeader
                                                headerComponent={
                                                    <CheckmateSameCaseGroupIcon className="text-blue" />
                                                }
                                                sortKey={SortColumns.SameCase.toString()}
                                                onSort={this.sortData}
                                                noWrap
                                            />
                                        )}
                                        <SortableHeader
                                            headerComponent={
                                                <i className="fal fa-exclamation-circle color-yellow" />
                                            }
                                            sortKey={SortColumns.Priority.toString()}
                                            onSort={this.sortData}
                                        />
                                        {Authorization.userHasRight(
                                            UserRightsEnum.ViewPackageSettlement,
                                            this.props.user,
                                            this.props.zoneContext.zoneGuid
                                        ) && (
                                            <SortableHeader
                                                headerComponent={<i className="fal fa-box-usd" />}
                                                sortKey={SortColumns.PackageSettlement.toString()}
                                                onSort={this.sortData}
                                            />
                                        )}
                                        <SortableHeader
                                            headerText="Status"
                                            sortKey={SortColumns.CaseStatus.toString()}
                                            onSort={this.sortData}
                                        />
                                        <SortableHeader
                                            headerText="Disease"
                                            sortKey={SortColumns.Diagnosis.toString()}
                                            onSort={this.sortData}
                                        />
                                        <SortableHeader
                                            headerText="State"
                                            sortKey={SortColumns.State.toString()}
                                            onSort={this.sortData}
                                        />
                                        <SortableHeader
                                            headerText="SQ Score"
                                            sortKey={SortColumns.QScore.toString()}
                                            onSort={this.sortData}
                                            noWrap
                                        />
                                        {Authorization.userHasRight(
                                            UserRightsEnum.ViewThreatScore,
                                            this.props.user,
                                            this.props.zoneContext.zoneGuid
                                        ) && (
                                            <SortableHeader
                                                headerText="Threat Score"
                                                sortKey={SortColumns.ThreatScore.toString()}
                                                onSort={this.sortData}
                                                noWrap
                                            />
                                        )}
                                        <SortableHeader
                                            headerText="Trial Date"
                                            sortKey={SortColumns.TrialDate.toString()}
                                            onSort={this.sortData}
                                            noWrap
                                        />
                                        <th />
                                        {showEasyUpdateColumn && <th />}
                                        {showAssessmentColumn && <th />}
                                        {/* Only checking for specific Planning & Execution Rights. Checking whether the logged in user has access to the case is done prior to here */}
                                        {showCasePlan && <th className="text-center">Case Plan</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.props.cases.map((caseItem: ICaseModel, i: number) => {
                                        const showCaseNameAsLink =
                                            Authorization.isAuthorizedToRoute(
                                                LocalRoutes.CaseFile,
                                                this.props.user,
                                                this.props.zoneContext.zoneGuid
                                            );

                                        const handleShowCaseGroupClick = (sameCaseGuid: string) => {
                                            this.props.zoneContext.showSameCaseModal(sameCaseGuid);
                                        };

                                        return (
                                            <tr key={i}>
                                                <td>
                                                    {showCaseNameAsLink ? (
                                                        <Link
                                                            to={this.props.zoneContext.buildZoneUrl(
                                                                LocalRoutes.CaseFile.replace(
                                                                    ':guid',
                                                                    caseItem!.guid!
                                                                )
                                                            )}
                                                            target="_blank"
                                                        >
                                                            {caseItem.caseName}
                                                        </Link>
                                                    ) : (
                                                        caseItem.caseName
                                                    )}
                                                </td>
                                                <td>{caseItem.caseNumber}</td>

                                                {showSameCaseColumn && (
                                                    <td>
                                                        {caseItem.sameCaseGuid && (
                                                            <CheckmateSameCaseGroupIcon
                                                                className="text-blue cursor-pointer"
                                                                onClick={() =>
                                                                    handleShowCaseGroupClick(
                                                                        caseItem.sameCaseGuid!
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                    </td>
                                                )}
                                                <td>
                                                    {caseItem.priority && (
                                                        <i className="fal fa-exclamation-circle color-yellow" />
                                                    )}
                                                </td>
                                                {viewPackageSettlementColumn && (
                                                    <td>
                                                        {caseItem.packageSettlement?.guid && (
                                                            <Link
                                                                target="_blank"
                                                                to={this.props.zoneContext.buildZoneUrl(
                                                                    LocalRoutes.PackageSettlementDetails.replace(
                                                                        ':guid',
                                                                        caseItem!.packageSettlement!
                                                                            .guid!
                                                                    )
                                                                )}
                                                            >
                                                                <i className="fal fa-box-usd color-black" />
                                                            </Link>
                                                        )}
                                                    </td>
                                                )}
                                                <td>
                                                    {caseItem.caseStatus && (
                                                        <span>
                                                            {caseItem!.caseStatus!.displayName}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>{caseItem.diagnosis}</td>
                                                <td>{caseItem.state}</td>
                                                <td>{caseItem.qScore}</td>
                                                {showThreatScoreColumn && (
                                                    <td>{caseItem!.threatScore}</td>
                                                )}
                                                <td>{Common.dateFormat(caseItem.trialDate)}</td>
                                                <td>
                                                    <button
                                                        className="btn-no-bg"
                                                        onClick={(
                                                            e: React.MouseEvent<HTMLButtonElement>
                                                        ) => {
                                                            this.handleCaseHistoryClicked(
                                                                e,
                                                                caseItem.guid!
                                                            );
                                                        }}
                                                    >
                                                        <CheckmateCaseHistoryIcon className="cursor-pointer" />
                                                    </button>
                                                </td>

                                                {showAssessmentColumn && (
                                                    <td className="align-top">
                                                        <Link
                                                            target="_blank"
                                                            to={`${this.props.zoneContext.buildZoneUrl(
                                                                LocalRoutes.CaseFile.replace(
                                                                    ':guid',
                                                                    caseItem.guid!
                                                                )
                                                            )}?ca=true`}
                                                        >
                                                            <i className="fal fa-lg fa-chess-bishop color-black"></i>
                                                        </Link>
                                                    </td>
                                                )}
                                                {showEasyUpdateColumn && (
                                                    <td className="text-center">
                                                        <button
                                                            className="btn-no-bg"
                                                            onClick={(
                                                                e: React.MouseEvent<HTMLButtonElement>
                                                            ) => {
                                                                this.handleEasyUpdate(
                                                                    e,
                                                                    caseItem.guid!
                                                                );
                                                            }}
                                                        >
                                                            <CheckmateEasyUpdateIcon
                                                                className={
                                                                    this.easyUpdateAttempted(
                                                                        caseItem.guid!
                                                                    )
                                                                        ? 'color-gray'
                                                                        : 'color-black'
                                                                }
                                                            />
                                                        </button>
                                                    </td>
                                                )}
                                                {showCasePlan && (
                                                    <td>
                                                        {caseItem.casePlan && (
                                                            <Link
                                                                to={this.props.zoneContext.buildZoneUrl(
                                                                    LocalRoutes.CasePlan.replace(
                                                                        ':guid',
                                                                        caseItem!.guid!
                                                                    )
                                                                )}
                                                            >
                                                                <i className="fal fa-ballot-check color-black" />
                                                            </Link>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                            <CheckmatePagination
                                pagedModel={this.mapToBasePagedModel(
                                    this.props.appliedSearchModel.queryModel,
                                    this.props.caseCount
                                )}
                                pagedModelPrefixText={'Showing'}
                                pagedModelSuffixText={'Cases'}
                                onPageChange={this.goToPage}
                                onPageSizeChange={this.handlePageSizeChange}
                            />
                        </>
                    )}
                </PageWithAffixedHeader>

                <CaseAuditControl
                    onCancel={() =>
                        this.setState({ showHistory: false, selectedCaseGuidToDisplayHistory: '' })
                    }
                    open={this.state.showHistory}
                    params={{
                        caseGuid: this.state.selectedCaseGuidToDisplayHistory ?? '',
                    }}
                    title={'Case Audit History'}
                />
            </>
        );
    }
}
